import { MessageSquare, Star } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';
import { setAdminReviewStatus, type AdminReviewRow } from '../../lib/apiClient';

/**
 * **Reviews** — the console's ninth and last section (spec §11).
 *
 * **No longer a PARITY TODO.** The populated row was previously guessed at,
 * because production holds no reviews and the specification could only record
 * what a running console showed. The row is fully written out in production's
 * own `pages/admin.tsx@11ca09a`, and what follows is copied from it: the five
 * stars, the status pill, the consent line, the `email · product · date` meta
 * line, the `Display as:` suffix, the body paragraph, and the three controls.
 *
 * **Three controls, not one, and none of them confirm.** Production offers
 * **Approve**, **Hide** and **Reset**, each shown only when the review is not
 * already in that state, and each fires immediately — this is the one console
 * surface with no two-step confirmation, because none of the three is
 * destructive and every one is reversible by the other two.
 *
 * The moderation semantics behind them are unchanged and were never a guess:
 * approving stamps `approved_at` and the acting administrator, and moving away
 * from approved clears both.
 */
const STATUS_STYLES: Record<string, string> = {
  approved: 'border-green-500/40 bg-green-500/10 text-green-500',
  hidden: 'border-destructive/40 bg-destructive/10 text-destructive',
  pending: 'border-border bg-secondary text-muted-foreground',
};

export function ReviewsSection({
  reviews,
  onChanged,
}: {
  reviews: AdminReviewRow[];
  onChanged: () => void;
}) {
  const { status, session } = useAuth();
  const token = status === 'signed-in' ? (session?.access_token ?? null) : null;

  return (
    <section className="flex flex-col gap-4" data-testid="admin-reviews">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-xl font-bold">
          <MessageSquare className="h-5 w-5 text-primary" aria-hidden="true" />
          Reviews
        </h2>
        <p className="text-sm text-muted-foreground" data-testid="reviews-summary">
          {`${reviews.length} total. Approve reviews to make them visible as public testimonials.`}
        </p>
      </div>

      {reviews.length === 0 ? (
        <p
          className="rounded-xl border border-border bg-card py-12 text-center text-sm text-muted-foreground"
          data-testid="reviews-empty"
        >
          No reviews submitted yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-4" data-testid="review-list">
          {reviews.map((review) => (
            <li
              key={review.id}
              className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4"
              data-testid="review-row"
              data-review-id={review.id}
              data-status={review.status}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Stars rating={review.rating ?? 0} />

                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                        STATUS_STYLES[review.status] ?? STATUS_STYLES.pending
                      }`}
                      data-testid="review-status"
                    >
                      {review.status}
                    </span>

                    {review.consentToPublish ? (
                      <span className="text-xs text-primary" data-testid="review-consent">
                        Consent given
                      </span>
                    ) : null}
                  </div>

                  {/*
                    Production's fallbacks, both of them: an unknown address
                    reads `unknown` and an unknown product reads `report`.
                  */}
                  <p className="text-xs text-muted-foreground" data-testid="review-meta">
                    {`${review.userEmail ?? 'unknown'} · ${review.productKey ?? 'report'} · ${
                      review.createdAt ? localeDate(review.createdAt) : ''
                    }`}
                    {review.displayName ? (
                      <span className="ml-2 text-foreground">{`Display as: ${review.displayName}`}</span>
                    ) : null}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  {review.status !== 'approved' ? (
                    <StatusButton
                      testId="action-review-approve"
                      label="Approve"
                      className="border-green-500/40 text-green-500 hover:bg-green-500/10"
                      onClick={() => void move(review.id, 'approved')}
                    />
                  ) : null}

                  {review.status !== 'hidden' ? (
                    <StatusButton
                      testId="action-review-hide"
                      label="Hide"
                      className="border-destructive/40 text-destructive hover:bg-destructive/10"
                      onClick={() => void move(review.id, 'hidden')}
                    />
                  ) : null}

                  {review.status !== 'pending' ? (
                    <StatusButton
                      testId="action-review-reset"
                      label="Reset"
                      className="border-transparent text-muted-foreground hover:text-foreground"
                      onClick={() => void move(review.id, 'pending')}
                    />
                  ) : null}
                </div>
              </div>

              <p className="text-sm leading-relaxed" data-testid="review-body">
                {review.body}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  async function move(reviewId: string, next: string) {
    if (!token) return;
    if (await setAdminReviewStatus(token, reviewId, next)) onChanged();
  }
}

/**
 * Five stars, filled up to the rating. Production renders all five always and
 * distinguishes them by fill, so the count never moves between rows.
 */
function Stars({ rating }: { rating: number }) {
  return (
    <span className="flex" data-testid="review-stars" data-rating={rating}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          aria-hidden="true"
          className={
            star <= rating ? 'h-4 w-4 fill-primary text-primary' : 'h-4 w-4 text-muted-foreground'
          }
        />
      ))}
      <span className="sr-only">{`${rating} out of 5`}</span>
    </span>
  );
}

function StatusButton({
  testId,
  label,
  className,
  onClick,
}: {
  testId: string;
  label: string;
  className: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${className}`}
    >
      {label}
    </button>
  );
}

function localeDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}
